// ============================================================
// DealNaicsReport.jsx — Deal NAICS Report (Neil only)
//
// Every deal, dead or live, with its NAICS industry and its total cost.
// Backed by GET /api/reports/deal-naics, which enforces the Neil-only gate
// server-side — the nav gate (`ownerEmail` in constants/nav.js) is cosmetic.
//
// Two things this screen is careful about:
//  • A NULL cost is UNKNOWN, not zero. A deal with no Sage location tagged has
//    no GL balance to report, and folding it in as $0 would understate nothing
//    while quietly implying the deal cost nothing. It renders "—" and is counted
//    apart in the header, so a total is always readable as "over N of M deals".
//  • A typed code and a derived one are shown DIFFERENTLY. 'user' is somebody's
//    decision, 'ai' is a reading the hourly sweep will revisit — rendering them
//    identically invites both being trusted equally (the DealNaicsChip rule).
//  • The TOTAL COST range follows the same rule as the cost cell: an unknown
//    cost is not zero, so it does not satisfy a numeric range and is excluded
//    while a bound is set — with a tick to opt those deals back in, and a line
//    saying how many were dropped. A "min $0" that swept them in would present
//    the deals we know nothing about as the cheapest in the book.
//  • "Budget / valuation" is whether the deal has a live row in EITHER
//    dbo.CapitalScheduleLines or dbo.DealDetailValuations, and the cell names
//    which one it found — a Yes nobody can trace back to a source is a number
//    people stop trusting. A null is "cannot say" (that source is not in this
//    database) and reads "—", never "No": the point of the column is to find the
//    deals carrying neither, so a wrong No is the one answer that would put a
//    fully budgeted deal on that list.
// ============================================================
import { useState, useMemo } from 'react';
import { B, font, radius, fonts, weight, space } from '../constants/brand';
import { api } from '../api/client';
import { fmt } from '../utils/format';
import { useApi } from '../hooks/useApi';
import { parseCostBound, matchesCost, hasCostFilter, describeCostFilter, isCostRangeInverted } from '../utils/costFilter';
import { Btn, Spinner, ErrorMsg, Table, PageHeader, Icon, Badge, StatusBadge, KpiCard } from '../components/ui';
import { NaicsEditModal } from '../components/shared/NaicsEditModal';
import { todayIsoInZone } from '@harvus/shared';

const DEAD_STATUSES = ['Dead', "Exit'd"];
const isDead = (d) => DEAD_STATUSES.includes(d.status);

const money = (v) => (v == null ? '—' : fmt.currency(v));

// Which sources answered yes, in the order they are read. Shown on screen and
// spelled out in the CSV — an exported report is read by people who cannot
// hover a chip to find out where a Yes came from.
const budgetSources = (d) => [
  d.hasSchedule  ? { key: 'schedule',  label: 'Capital schedule', n: d.budgetLines }    : null,
  d.hasValuation ? { key: 'valuation', label: 'Valuation',        n: d.valuationCount } : null,
].filter(Boolean);

// Yes (with its sources) / No / cannot say — the same words on screen and in
// the CSV. An empty string is "unknown", never "No" (see the header).
const budgetLabel = (d) => {
  if (d.hasBudget == null) return '';
  if (!d.hasBudget) return 'No';
  const src = budgetSources(d).map(s => s.label.toLowerCase()).join(', ');
  return src ? `Yes (${src})` : 'Yes';
};

// Why a cell reads "—": name the source that could not be asked, rather than
// leaving an unexplained dash.
const unknownBudgetReason = (d, data) => {
  const missing = [
    d.hasSchedule  == null && data?.budgetAvailable    === false ? 'capital schedules (migration 164)' : null,
    d.hasValuation == null && data?.valuationAvailable === false ? 'valuations' : null,
  ].filter(Boolean);
  return missing.length
    ? `Not present in this database: ${missing.join(' and ')}. Unknown, not "no".`
    : 'One of the two sources could not be read — unknown, not "no".';
};

// Where a code came from, in the same words on screen and in the CSV — an
// exported report is read by people who never saw the chip.
const SOURCE_LABEL = { user: 'Entered', ai: 'From documents' };
const sourceLabel = (s) => (s ? SOURCE_LABEL[s] || s : '');

// Report columns — one source of truth for the table and the CSV export.
const COLUMNS = [
  { key: 'name',   label: 'Deal',       value: d => d.name || '' },
  { key: 'status', label: 'Status',     value: d => d.status || '' },
  { key: 'naicsCode', label: 'NAICS',   value: d => d.naicsCode || '' },
  { key: 'naicsDescription', label: 'Industry (NAICS description)', value: d => d.naicsDescription || '' },
  // The second axis, and it is the deal detail's EXISTING Property Type — not a
  // new taxonomy. NAICS says what the business does, and every property holding
  // leases property, so a land-heavy book collapses into 531190 "Lessors of
  // Other Real Estate Property"; Property Type is where R/E deals are told
  // apart at all. Blank for a deal holding no R/E detail, which is not the same
  // as a deal whose R/E detail has no type set.
  { key: 'assetTypes', label: 'Property Type (R/E)', value: d => d.assetTypes || '' },
  { key: 'naicsSource', label: 'Source', value: d => sourceLabel(d.naicsSource) },
  { key: 'hasBudget', label: 'Budget / valuation', value: d => budgetLabel(d) },
  { key: 'totalCost', label: 'Total Cost', align: 'right',
    value: d => money(d.totalCost), csv: d => d.totalCost ?? '' },
];

function exportCsv(rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map(c => c.label).join(',');
  const body = rows.map(d => COLUMNS.map(c => esc((c.csv || c.value)(d))).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `deal-naics-report-${todayIsoInZone()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// A derived code is provisional by design, so it reads as a reading rather
// than as a fact.
const SourceChip = ({ source }) => {
  if (!source) return null;
  const user = source === 'user';
  return <Badge color={user ? B.navy : B.tealDk}>{sourceLabel(source)}</Badge>;
};

// The asks that come up; the boxes below handle everything else. Values are the
// same strings the inputs hold, so a preset and a typed range are one state.
const COST_PRESETS = [
  { label: '< $100k',       min: '',     max: '100k' },
  { label: '$100k – $1m',   min: '100k', max: '1m' },
  { label: '$1m – $10m',    min: '1m',   max: '10m' },
  { label: '> $10m',        min: '10m',  max: '' },
];

const costInputStyle = {
  padding: '5px 8px', borderRadius: radius.md, border: `1px solid ${B.border}`,
  fontSize: font.sm, width: 88, fontFamily: fonts.sans, background: B.surface, outline: 'none',
};

const FILTERS = [
  { id: 'all',  label: 'All deals' },
  { id: 'live', label: 'Live only' },
  { id: 'dead', label: "Dead / Exit'd" },
  { id: 'unclassified', label: 'No NAICS' },
];

export const DealNaicsReport = ({ onDealClick, user }) => {
  const { data, loading, error, reload } = useApi(() => api.get('reports/deal-naics'), []);

  const [editing, setEditing] = useState(null);       // the deal row being edited
  // Codes changed in this session, applied over the fetched rows. useApi has no
  // setter, and re-fetching the whole book after every single edit would make
  // correcting a run of deals unusable — which is the job this page exists for.
  const [edits, setEdits] = useState({});             // dealID -> { naicsCode, naicsDescription, naicsSource }
  const canEdit = user?.role === 'Admin';
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [showRollup, setShowRollup] = useState(false);
  // The TYPED TEXT is state, not the parsed number — a half-typed "1.2" must
  // survive a re-render, and clearing the box has to really clear the bound.
  const [minCost, setMinCost] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [includeUnknownCost, setIncludeUnknownCost] = useState(false);

  const deals = useMemo(
    () => (data?.deals || []).map(d => (edits[d.dealID] ? { ...d, ...edits[d.dealID] } : d)),
    [data, edits]);

  const costRange = useMemo(() => ({
    min: parseCostBound(minCost),
    max: parseCostBound(maxCost),
    includeUnknown: includeUnknownCost,
  }), [minCost, maxCost, includeUnknownCost]);

  const costActive   = hasCostFilter(costRange);
  const costInverted = isCostRangeInverted(costRange);
  // Text in a box that is not a bound. Left unsaid, this reads as a filter that
  // does not work: "1.2.3" in Min simply filters nothing, which looks identical
  // to an empty box that is doing exactly what it should.
  const unreadableBounds = [
    minCost.trim() && costRange.min == null ? 'Min' : null,
    maxCost.trim() && costRange.max == null ? 'Max' : null,
  ].filter(Boolean);

  // The status tabs, applied on their own — both the row list and the
  // "dropped for want of a cost" count are taken from this same set, so the
  // number beside the filter and the rows on screen cannot describe different
  // books.
  const statusFiltered = useMemo(() => {
    if (filter === 'live') return deals.filter(d => !isDead(d));
    if (filter === 'dead') return deals.filter(isDead);
    if (filter === 'unclassified') return deals.filter(d => !d.naicsCode);
    return deals;
  }, [deals, filter]);

  const rows = useMemo(() => {
    const out = statusFiltered.filter(d => matchesCost(d, costRange));

    // TOKEN MATCHING over the whole row, not one substring in one field.
    //
    // Two things were wrong with a per-field `includes(needle)`. It could not
    // match across fields, so "riverbend poultry" — a deal name plus its
    // industry — found nothing. And it only saw a code's OWN title and its
    // sector, so "poultry" found nothing for 112310, whose own parent level is
    // literally "Poultry and Egg Production", and "lessors of real estate"
    // found nothing for 531110 despite being an exact intermediate title.
    //
    // naicsPath carries every level of the code's hierarchy, which is what
    // makes searching by the WORDS of an industry work at all. Every term must
    // appear somewhere (AND), so adding a word narrows rather than widens.
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return out;
    return out.filter(d => {
      const hay = [d.name, d.status, d.naicsCode, d.naicsDescription,
                   d.naicsSectorTitle, d.naicsPath, d.assetTypes]
        .filter(Boolean).join(' ').toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [statusFiltered, q, costRange]);

  // How many deals the range dropped for having no cost at all, so an
  // unknown-cost deal is never silently absent from a filtered view.
  const droppedUnknownCost = useMemo(() => (
    costActive && !includeUnknownCost
      ? statusFiltered.filter(d => d.totalCost == null).length
      : 0
  ), [statusFiltered, costActive, includeUnknownCost]);

  // Totals for whatever is on screen. Computed over the VISIBLE rows on purpose
  // — a filtered view whose total still described the whole book would be worse
  // than no total. The header says how many of those rows carry a cost at all.
  const shown = useMemo(() => {
    const withCost = rows.filter(d => d.totalCost != null);
    return {
      count: rows.length,
      cost: withCost.reduce((s, d) => s + d.totalCost, 0),
      withCost: withCost.length,
      classified: rows.filter(d => d.naicsCode).length,
    };
  }, [rows]);

  // Counted the same way the "Dead / Exit'd" filter selects, so the header and
  // the tabs cannot describe the same book differently.
  const deadCount = useMemo(() => deals.filter(isDead).length, [deals]);

  const tableColumns = COLUMNS.map(c => ({
    key: c.key,
    label: c.label,
    align: c.align,
    render: (_v, d) => {
      if (c.key === 'name') {
        return d.dealID && onDealClick ? (
          <button onClick={() => onDealClick(d.dealID)}
            style={{ background: 'none', border: 'none', padding: 0, color: B.navy, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.base, fontWeight: weight.medium, textAlign: 'left', textDecoration: 'underline' }}>
            {d.name}
          </button>
        ) : (d.name || '—');
      }
      if (c.key === 'status') return d.status ? <StatusBadge status={d.status} /> : '—';
      if (c.key === 'naicsCode') {
        const body = d.naicsCode ? (
          <span style={{ fontFamily: fonts.mono || 'monospace', fontSize: font.sm }}>
            {d.naicsCode}
            {d.naicsRetired && (
              <span title="Not in the 2022 NAICS table — an earlier vintage."
                style={{ marginLeft: 6, color: B.amber, fontSize: font.xs }}>†</span>
            )}
          </span>
        ) : (
          // "Set" rather than a dash: an unclassified row is the one somebody
          // opened this report to fix, so it carries the affordance most visibly.
          <span style={{ color: canEdit ? B.navy : B.textLt, fontSize: font.sm }}>
            {canEdit ? 'Set…' : '—'}
          </span>
        );
        if (!canEdit) return body;
        return (
          <button onClick={() => setEditing(d)} title="Set this deal's NAICS code"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                     font: 'inherit', textAlign: 'left', textDecoration: 'underline',
                     textUnderlineOffset: 3, textDecorationStyle: 'dotted' }}>
            {body}
          </button>
        );
      }
      if (c.key === 'naicsDescription') {
        if (!d.naicsDescription) return <span style={{ color: B.textLt }}>Not classified</span>;
        // The hierarchy above the code, minus the leaf (already the line above).
        // Shown because the search reads it: a row matching on "poultry" has to
        // be able to show WHERE the word came from.
        const parents = (d.naicsPath || '').split(' \u203a ').slice(0, -1).join(' \u203a ');
        return (
          <div>
            <div>{d.naicsDescription}</div>
            {parents && (
              <div title={d.naicsPath}
                style={{ fontSize: font.xs, color: B.textMd, marginTop: 2, lineHeight: 1.35 }}>
                {parents}
              </div>
            )}
          </div>
        );
      }
      if (c.key === 'assetTypes') {
        if (!d.assetTypes) return <span style={{ color: B.textLt }}>—</span>;
        return (
          <div style={{ fontSize: font.sm }}>
            {d.assetTypes.split(', ').map(t => (
              <div key={t} style={{ lineHeight: 1.35 }}>{t}</div>
            ))}
          </div>
        );
      }
      if (c.key === 'naicsSource') {
        return d.naicsSource
          ? <SourceChip source={d.naicsSource} />
          : <span style={{ color: B.textLt }}>—</span>;
      }
      if (c.key === 'hasBudget') {
        if (d.hasBudget == null) {
          return <span style={{ color: B.textLt }} title={unknownBudgetReason(d, data)}>—</span>;
        }
        if (!d.hasBudget) return <span style={{ color: B.textLt }}>No</span>;
        // A Yes names its source(s) and how many rows are behind it, so it can
        // be traced back to the tab that holds them. A Yes with no nameable
        // source cannot arise from eitherHas, but an empty cell would be worse
        // than a bare chip if it ever did.
        const sources = budgetSources(d);
        if (!sources.length) return <Badge color={B.tealDk}>Yes</Badge>;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {sources.map(sc => (
              <Badge key={sc.key} color={sc.key === 'schedule' ? B.tealDk : B.navy}>
                {`${sc.label} · ${sc.n}`}
              </Badge>
            ))}
          </div>
        );
      }
      if (c.key === 'totalCost') {
        return d.totalCost == null
          ? <span style={{ color: B.textLt }} title="No Sage location tagged on this deal — no GL balance to report.">—</span>
          : money(d.totalCost);
      }
      return c.value(d);
    },
  }));

  const rollupColumns = [
    { key: 'naicsCode', label: 'NAICS', render: (_v, g) => g.naicsCode || <span style={{ color: B.textLt }}>Unclassified</span> },
    { key: 'naicsDescription', label: 'Industry', render: (_v, g) => g.naicsDescription || <span style={{ color: B.textLt }}>—</span> },
    { key: 'dealCount', label: 'Deals', align: 'right', render: (_v, g) => g.dealCount },
    { key: 'totalCost', label: 'Total Cost', align: 'right',
      render: (_v, g) => (
        <span>
          {money(g.totalCost)}
          {g.withoutCost > 0 && (
            <span style={{ color: B.textMd, fontSize: font.xs, marginLeft: 6 }}>({g.withoutCost} w/o cost)</span>
          )}
        </span>
      ) },
  ];

  return (
    <div>
      <PageHeader
        icon="reports"
        title="Deal NAICS Report"
        subtitle={`${shown.count} deal${shown.count !== 1 ? 's' : ''} · ${money(shown.cost)} total cost across ${shown.withCost}`}
        actions={
          <>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 10, display: 'flex', color: B.textLt, pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search deals, codes, or industry words — e.g. “poultry”"
                style={{ padding: '8px 12px 8px 32px', borderRadius: radius.md, border: `1px solid ${B.border}`, fontSize: font.base, minWidth: 240, fontFamily: fonts.sans, background: B.surface, outline: 'none' }}
              />
            </div>
            <Btn size="sm" variant="ghost" onClick={() => setShowRollup(v => !v)}>
              {showRollup ? 'Show deals' : 'Roll up by NAICS'}
            </Btn>
            <Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>
            <Btn size="sm" disabled={rows.length === 0} onClick={() => exportCsv(rows)}><Icon name="download" size={14} /> Export CSV</Btn>
          </>
        }
      />

      {error ? <ErrorMsg msg={error} onRetry={reload} /> : loading ? <Spinner /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: space.lg, marginBottom: space.xl }}>
            <KpiCard label="Deals" value={data?.totals?.deals ?? 0}
              sub={`${deadCount} dead/exited · ${(data?.totals?.deals || 0) - deadCount} live`} />
            <KpiCard label="Total cost" value={money(data?.totals?.totalCost)} color={B.tealDk}
              sub={data?.totals?.withoutCost ? `${data.totals.withoutCost} deal(s) with no GL balance` : 'across every deal'} />
            <KpiCard label="Classified" value={data?.totals?.classified ?? 0} color={B.navy}
              sub={`${data?.totals?.unclassified ?? 0} without a NAICS code`} />
          </div>

          {/* The cost figures are a cached snapshot, recomputed after each Sage
              import and by a daily timer — never live-aggregated per request.
              Saying so is the difference between a stale number and a wrong one. */}
          {data?.costAsOfUTC && (
            <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.lg }}>
              Cost figures as of {fmt.date(data.costAsOfUTC)} — recomputed after each Sage import and daily.
            </div>
          )}
          {data?.naicsAvailable === false && (
            <div style={{ fontSize: font.sm, color: B.amber, marginBottom: space.lg }}>
              NAICS columns are not present in this database (migration 169 has not been applied) — industries will be blank.
            </div>
          )}
          {(data?.budgetAvailable === false || data?.valuationAvailable === false) && (
            <div style={{ fontSize: font.sm, color: B.amber, marginBottom: space.lg }}>
              {data?.budgetAvailable === false && data?.valuationAvailable === false
                ? 'Neither capital schedules (migration 164) nor valuations are present in this database'
                : data?.budgetAvailable === false
                  ? 'Capital schedules are not present in this database (migration 164 has not been applied)'
                  : 'Valuations are not present in this database'}
              {' — the Budget / valuation column reads “—” where it could not ask, which means unknown rather than no.'}
            </div>
          )}

          {!showRollup && (
            <div style={{ marginBottom: space.lg }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {FILTERS.map(f => (
                  <Btn key={f.id} size="sm" variant={filter === f.id ? 'primary' : 'ghost'} onClick={() => setFilter(f.id)}>
                    {f.label}
                  </Btn>
                ))}
              </div>

              {/* Total cost is a RANGE, not another mutually exclusive tab, so it
                  sits on its own row and stacks with whichever tab is active.
                  Presets are the common asks; the boxes take anything, and
                  accept the shorthand people write beside a dollar figure. */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: space.sm }}>
                <span style={{ fontSize: font.sm, color: B.textMd }}>Total cost</span>
                {COST_PRESETS.map(pre => {
                  const on = minCost === pre.min && maxCost === pre.max;
                  return (
                    <Btn key={pre.label} size="sm" variant={on ? 'primary' : 'ghost'}
                      onClick={() => { setMinCost(pre.min); setMaxCost(pre.max); }}>
                      {pre.label}
                    </Btn>
                  );
                })}
                <input value={minCost} onChange={e => setMinCost(e.target.value)}
                  placeholder="Min" aria-label="Minimum total cost" style={costInputStyle} />
                <span style={{ fontSize: font.sm, color: B.textLt }}>to</span>
                <input value={maxCost} onChange={e => setMaxCost(e.target.value)}
                  placeholder="Max" aria-label="Maximum total cost" style={costInputStyle} />
                {costActive && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: font.sm, color: B.textMd, cursor: 'pointer' }}>
                    <input type="checkbox" checked={includeUnknownCost}
                      onChange={e => setIncludeUnknownCost(e.target.checked)} />
                    Include no recorded cost
                  </label>
                )}
                {(costActive || minCost || maxCost) && (
                  <Btn size="sm" variant="ghost"
                    onClick={() => { setMinCost(''); setMaxCost(''); setIncludeUnknownCost(false); }}>
                    Clear
                  </Btn>
                )}
              </div>

              {/* An active range never filters silently: it says what it is doing,
                  and says how many deals it dropped for having no cost at all. */}
              {unreadableBounds.length ? (
                <div style={{ fontSize: font.sm, color: B.red, marginTop: space.sm }}>
                  {unreadableBounds.join(' and ')} {unreadableBounds.length > 1 ? 'are' : 'is'} not
                  a number — try 250000, 250k or $1.5m. {unreadableBounds.length > 1 ? 'They are' : 'It is'} being ignored.
                </div>
              ) : costInverted ? (
                <div style={{ fontSize: font.sm, color: B.red, marginTop: space.sm }}>
                  The minimum is above the maximum, so nothing can match — swap them.
                </div>
              ) : costActive && (
                <div style={{ fontSize: font.sm, color: B.textMd, marginTop: space.sm }}>
                  Showing deals costing {describeCostFilter(costRange, money)}.
                  {droppedUnknownCost > 0 && (
                    <>
                      {' '}{droppedUnknownCost} deal{droppedUnknownCost !== 1 ? 's' : ''} with no
                      recorded cost {droppedUnknownCost !== 1 ? 'are' : 'is'} excluded — an unknown
                      cost is not $0.
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* The roll-up is the SERVER's, computed over the whole book — so it
              must say so while a filter is narrowing the deal list, or the two
              views read as contradicting each other. */}
          {showRollup && (costActive || filter !== 'all' || q.trim()) && (
            <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: space.lg }}>
              This roll-up covers every deal. The filters and search apply to the deal list —
              switch back with “Show deals” to see them applied.
            </div>
          )}

          {showRollup
            ? <Table columns={rollupColumns} data={data?.byNaics || []} />
            : <Table columns={tableColumns} data={rows} virtualize />}
        </>
      )}

      {editing && (
        <NaicsEditModal
          deal={editing}
          onClose={() => setEditing(null)}
          // Patched locally rather than re-fetching: correcting a run of deals
          // is the job here, and a full reload between each one would make it
          // unusable. The KPI counts come from the server's totals and so do
          // not move until the next Refresh — which is honest, since they are
          // computed over the whole book, not over what is on screen.
          onSaved={(row) => setEdits(prev => ({ ...prev, [row.dealID]: row }))}
        />
      )}
    </div>
  );
};

export default DealNaicsReport;
